"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { rolesAll } from "../api/controllers/roles";
import { obtenerPaises } from "../api/controllers/paises";
import { obtenerMonedas } from "../api/controllers/monedas";
import { obtenerLocalidades } from "../api/controllers/localidades";
import { deportesAll } from "../api/controllers/deportes";
import WelcomeModal from "../components/ModalWelcome/ModalWelcome";
import { toast } from "react-toastify";
import SignUpForm from "../components/SignUp/SignUpForm";
import SideAnimation from "../components/SignUp/SideAnimation";
import SignUpModal from "../components/SignUp/SignUpModal";
import { containerVariants } from "../components/SignUp/animations";
import UsuarioSignUp from "./usuario/UsuarioSignUp";
import EscuelaSignUp from "./escuela/EscuelaSignUp";
import InstructorSignUp from "./instructor/InstructorSignUp";

const globalStyles = `
  [data-radix-popper-content-wrapper] {
    z-index: 9999999 !important;
    position: fixed !important;
    transform-origin: top center !important;
    animation: none !important;
  }
  
  .radix-select-content {
    overflow-y: auto !important;
    max-height: 200px !important;
  }
  
  body.select-open {
    overflow: hidden !important;
  }
`;

interface UserType {
  rolId: number;
  codigo: number;
  authority: string;
  descripcion: string;
  permisos: string;
}

export default function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState<UserType | null>(null);
  const [step, setStep] = useState(1);
  const [open, setOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [rol, setRol] = useState<any[]>([]);
  const [deportes, setDeportes] = useState<any[]>([]);
  const [monedas, setMonedas] = useState<any[]>([]);
  const [paises, setPaises] = useState<any[]>([]);
  const [localidades, setLocalidades] = useState<any[]>([]);
  const [selectedPaisId, setSelectedPaisId] = useState<number>(9);

  const slideTexts = [
    "Conecta con la comunidad",
    "Encuentra las mejores olas",
    "Comparte tu pasión"
  ];

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
      document.body.classList.remove('select-open');
    } else if (!isSelectOpen) {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('select-open');
    };
  }, [showModal, isSelectOpen]);

  useEffect(() => {
    if (isSelectOpen) {
      document.body.classList.add('select-open');
    } else if (!showModal) {
      document.body.classList.remove('select-open');
    }
  }, [isSelectOpen, showModal]);

  useEffect(() => {
    const bubbles = document.querySelector('.bubbles') as HTMLElement;
    if (bubbles) {
      bubbles.style.display = 'none';
    }

    return () => {
      if (bubbles) {
        bubbles.style.display = 'block';
      }
    };
  }, []);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = globalStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const fetchRol = async () => {
    try {
      const data = await rolesAll();
      const filteredRoles = data.filter((role: { authority: string }) =>
        !role.authority.includes("SHOP") &&
        !role.authority.includes("TIENDA")
      );
      setRol(filteredRoles);
    } catch (err) {
      setError("Hubo un error al obtener los Roles.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaises = async () => {
    try {
      const data = await obtenerPaises();
      setPaises(data);
    } catch (err) {
      setError("Hubo un error al obtener los paises.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonedas = async () => {
    try {
      const data = await obtenerMonedas();
      setMonedas(data);
    } catch (err) {
      setError("Hubo un error al obtener los monedas.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocalidades = async (paisId: number) => {
    try {
      const data = await obtenerLocalidades(paisId);
      setLocalidades(data);
    } catch (err) {
      setError("Hubo un error al obtener las localidades.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDeportes = async () => {
    try {
      const data = await deportesAll();
      setDeportes(data);
    } catch (err) {
      setError("Hubo un error al obtener los deportes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRol();
    fetchPaises();
    fetchMonedas();
    fetchLocalidades(selectedPaisId);
    fetchDeportes();
  }, [selectedPaisId]);

  useEffect(() => {
    fetchLocalidades(selectedPaisId);
  }, [selectedPaisId]);

  const handleUserTypeChange = (rolId: string) => {
    const selectedRole = rol.find((role) => role.rolId.toString() === rolId);

    if (selectedRole) {
      setUserType({
        rolId: selectedRole.rolId,
        codigo: selectedRole.rolId,
        authority: selectedRole.authority || "",
        descripcion: selectedRole.descripcion || "",
        permisos: selectedRole.permisos || "",
      });
    }
  };

  const handleNextStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Por favor ingrese su correo electrónico");
      return;
    }

    if (!password.trim()) {
      toast.error("Por favor ingrese una contraseña");
      return;
    }

    if (!userType) {
      toast.error("Por favor seleccione un tipo de usuario");
      return;
    }

    setStep(2);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setStep(1);
  };

  const handleBackStep = () => {
    setStep(1);
    setShowModal(false);
  };

  const renderModalContent = () => {
    if (userType && userType.rolId === 2) {
      return (
        <UsuarioSignUp
          email={email}
          password={password}
          step={step}
          setStep={handleBackStep}
          setOpen={setOpen}
          roleData={userType}
        />
      );
    } else if (userType && userType.rolId === 3) {
      return (
        <InstructorSignUp
          email={email}
          password={password}
          step={step}
          setStep={handleBackStep}
          setOpen={setOpen}
          roleData={userType}
          monedas={monedas}
          deportes={deportes}
          paises={paises}
          localidades={localidades}
          onPaisChange={(paisId: number) => {
            setSelectedPaisId(paisId);
          }}
        />
      );
    } else if (userType && userType.rolId === 4) {
      return (
        <EscuelaSignUp
          email={email}
          password={password}
          step={step}
          setStep={handleBackStep}
          setOpen={setOpen}
          roleData={userType}
          monedas={monedas}
          deportes={deportes}
          paises={paises}
          localidades={localidades}
          onPaisChange={(paisId: number) => {
            setSelectedPaisId(paisId);
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="flex h-full min-h-screen md:h-[92dvh] w-full relative !z-[999999] overflow-hidden bg-white">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="w-full h-full overflow-hidden z-[9999] border-none shadow-2xl bg-white"
        style={{
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 40px rgba(0, 0, 0, 0.1) inset"
        }}
      >
        <div className="grid md:grid-cols-2 min-h-screen md:h-[92dvh]  ">
          <SideAnimation slideTexts={slideTexts} />

          <div className="p-4 md:p-10  relative bg-gradient-to-br from-white via-blue-50 to-indigo-100 flex items-center justify-center">
            <div className="absolute top-0 right-0 w-60 h-60 bg-gradient-to-br from-blue-300/10 to-indigo-300/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-gradient-to-tr from-blue-300/10 to-indigo-300/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
            <motion.div
              animate={{
                opacity: [0.5, 0.8, 0.5],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-gradient-to-r from-blue-400/5 to-indigo-400/5 rounded-full blur-3xl"
            />

            <div className="relative z-10 w-full max-w-md">
                {step === 1 && (
                  <SignUpForm
                    email={email}
                    setEmail={setEmail}
                    password={password}
                    setPassword={setPassword}
                    userType={userType}
                    handleUserTypeChange={handleUserTypeChange}
                    handleNextStep={handleNextStep}
                    rol={rol}
                    isSelectOpen={isSelectOpen}
                    setIsSelectOpen={setIsSelectOpen}
                  />
                )}
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <SignUpModal
            showModal={showModal}
            handleCloseModal={handleCloseModal}
            handleBackStep={handleBackStep}
          >
            {renderModalContent()}
          </SignUpModal>
        )}
      </AnimatePresence>

      {open && (
        <div className="fixed inset-0 !z-[9999999] backdrop-blur-[40px] bg-[#0000006e] flex items-center justify-center">
          <WelcomeModal open={open} onOpenChange={setOpen} />
        </div>
      )}
    </div>
  );
}
